import { useMutation, useQuery } from '@tanstack/react-query';

import { getApiErrorMessage } from '@/lib/api/client';
import { getData, postFormData } from '@/lib/api/client-v2';
import { DATE_ISO, formatDate } from '@/lib/dates';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import { ENDPOINTS } from '@/types/api/endpoints';

/*
 * Résiliation d'un contrat, reprise du middleware redux `terminate`.
 */

export type TerminateForm = Record<string, unknown> & { files?: Record<string, File | undefined> };

export interface TerminateVariables {
    contractId: string | number;
    form: TerminateForm;
    successFunction?: () => void;
}

/**
 * Corps multipart : valeurs vides ignorées, pièces en `files[code]`, prénom / nom omis s'ils sont
 * vides, dates en `yyyy-MM-dd` (format exigé par la v2).
 */
const toTerminateFormData = (body: TerminateForm): FormData => {
    const data = new FormData();

    Object.keys(body).forEach((key) => {
        const value = body[key];
        if (value === undefined || value === null) {
            return;
        }
        if (key === 'files') {
            const files = value as Record<string, File | undefined>;
            Object.keys(files).forEach((doc) => {
                data.append(`files[${doc}]`, files[doc] as Blob | string);
            });
        } else if (value instanceof Date) {
            data.append(key, formatDate(value, DATE_ISO));
        } else if (key === 'prenom' || key === 'nom') {
            if (value !== '') {
                data.append(key, value as string);
            }
        } else {
            data.append(key, value as string);
        }
    });

    return data;
};

export const useTerminate = () =>
    useMutation({
        mutationFn: ({ contractId, form }: TerminateVariables) =>
            postFormData<null>(ENDPOINTS.contractResiliationRequest(contractId), toTerminateFormData(form)),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: '<div><p>Un accusé de réception a été envoyé par email</p></div>',
                type: 'success',
                title: 'Demande envoyée',
                confirmButtonText: "J'ai compris",
                successFunction,
            } as Parameters<typeof notify>[0]);
        },
        onError: (error) => {
            void notify({
                html: '<p>' + getApiErrorMessage(error) + '</p>',
                icon: 'error',
                title: 'Attention !',
                confirmButtonText: "J'ai compris",
            });
        },
    });

/**
 * Lien de prise de rendez-vous « fidélisation » (`GET /booking/fidelisation`, `data.url`),
 * relu à chaque ouverture du formulaire de résiliation. Échec silencieux.
 */
export const useBookingFidelisationUrl = (contractId: string | number | null | undefined) =>
    useQuery({
        queryKey: queryKeys.bookingFidelisation(contractId),
        queryFn: () =>
            getData<{ url?: string } | null>(
                ENDPOINTS.booking('fidelisation'),
                contractId ? { contract_id: contractId } : undefined,
            ).then((data) => data?.url ?? null),
        refetchOnMount: 'always',
    });
